import { useState, useEffect, FormEvent, useRef } from 'react';
import { Loader2 } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { setup2FA, verify2FA, ApiException } from '../api/client';
import { useAuth } from '../App';
import { sichereRuecksprungAdresse } from '../lib/weiterleitung';
import { Karte, Notiz } from '../components/leitstand';

/**
 * Zwei-Faktor-Anmeldung einrichten.
 *
 * Laeuft noch vor der fertigen Anmeldung — kein Benutzer, keine Navigation.
 * Darum die mittige Karte im Leitstand-Stil statt des vollen Rahmens.
 *
 * QR-Code scannen, ersatzweise den Schluessel abtippen, danach einmal den
 * Code bestaetigen. Die Bestaetigung ist der Beweis, dass die App wirklich
 * eingerichtet ist — ohne sie liesse sich jemand aussperren.
 */

export default function TwoFactorSetupPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refresh } = useAuth();

  // returnTo aus URL Query Parameter
  const returnTo = sichereRuecksprungAdresse(searchParams.get('returnTo'));
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const setupCalled = useRef(false);

  useEffect(() => {
    if (setupCalled.current) return;
    setupCalled.current = true;
    loadSetup();
  }, []);

  const loadSetup = async () => {
    try {
      const data = await setup2FA();
      setQrCode(data.qrCodeDataUrl);
      setSecret(data.secret);
    } catch (err) {
      if (err instanceof ApiException) {
        setError(err.data.error);
      } else {
        setError('Die Einrichtung lässt sich gerade nicht laden. Bitte die Seite neu laden.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (index: number, value: string) => {
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pastedData.length === 6) {
      setCode(pastedData.split(''));
    }
  };

  const handleVerify = async (fullCode?: string) => {
    const codeToVerify = fullCode || code.join('');
    if (codeToVerify.length !== 6) return;

    setError('');
    setVerifying(true);

    try {
      await verify2FA(codeToVerify);
      // WICHTIG: AuthContext refreshen bevor Navigation, sonst ist user noch null
      await refresh();
      navigate(returnTo);
    } catch (err) {
      if (err instanceof ApiException) {
        setError(
          err.data.error || 'Der Code stimmt nicht. Bitte den aktuellen Code aus der App eingeben.',
        );
      } else {
        setError('Keine Verbindung zum Server. Bitte noch einmal versuchen.');
      }
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setVerifying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-grund text-tinte flex items-center justify-center p-4">
        <Loader2 className="w-8 h-8 animate-spin text-tinte-3" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-grund text-tinte flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        <p className="mb-5 text-center font-zahl text-[15px] font-semibold tracking-[0.06em] text-tinte-2">
          wash<b className="font-semibold text-akzent">·</b>ops
        </p>

        <Karte titel="Zwei-Faktor-Anmeldung einrichten">
          <p className="text-tinte-2 text-center mb-6">
            Scannen Sie den QR-Code mit Ihrer Authenticator-App
          </p>

          {error && (
            <div className="mb-6 p-4 rounded-md border border-stoer bg-flaeche-2">
              <p className="text-stoer text-sm">{error}</p>
            </div>
          )}

          {/* Der QR bringt seinen hellen Grund selbst mit — er muss hell
              bleiben, sonst liest ihn keine Kamera. Der Rahmen setzt ihn
              nur ab. */}
          {qrCode && (
            <div className="flex justify-center mb-6">
              <img
                src={qrCode}
                alt="QR-Code für die Authenticator-App"
                className="w-48 h-48 rounded-lg border border-linie-2"
              />
            </div>
          )}

          {/* Ersatzweg fuer Kameras, die nicht wollen */}
          <div className="mb-6">
            <button
              type="button"
              onClick={() => setShowManual(!showManual)}
              className="text-sm text-akzent transition-colors hover:text-tinte"
            >
              {showManual ? 'QR-Code scannen' : 'Code manuell eingeben'}
            </button>

            {showManual && secret && (
              <div className="mt-3">
                <Notiz titel="Geheimer Schlüssel">
                  <code className="font-zahl text-sm break-all text-tinte">{secret}</code>
                </Notiz>
              </div>
            )}
          </div>

          <form
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              handleVerify();
            }}
          >
            <p className="text-sm text-tinte-2 mb-3 text-center">
              Geben Sie den 6-stelligen Code ein:
            </p>

            <div className="flex justify-center gap-3 mb-6" onPaste={handlePaste}>
              {code.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => {
                    inputRefs.current[index] = el;
                  }}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  className="w-12 h-14 text-center text-2xl font-bold font-zahl tabular-nums border-2 border-linie-2 bg-flaeche-2 text-tinte rounded-lg focus:border-akzent outline-none transition-all disabled:opacity-50"
                  disabled={verifying}
                />
              ))}
            </div>

            <button
              type="submit"
              disabled={verifying || code.some((d) => !d)}
              className="w-full min-h-[48px] rounded-lg border border-akzent bg-akzent px-6 py-3 font-medium text-akzent-satz transition-colors disabled:opacity-50"
            >
              {verifying ? 'Wird geprüft...' : 'Aktivieren'}
            </button>
          </form>
        </Karte>
      </div>
    </div>
  );
}
